import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { useAppStore } from '../stores/appStore';

/**
 * Ensures selectedBabyId always references a valid baby.
 * Returns the babies list for optional use by the caller.
 */
export function useValidatedBabyId() {
  const { selectedBabyId, setSelectedBabyId } = useAppStore();

  const { data: babies = [], isFetched, isLoading, status } = useQuery({
    queryKey: ['babies'],
    queryFn: () => apiGet('/babies'),
    select: (res) => res.babies || res || [],
  });

  const isValid = babies.length > 0 && babies.some((b) => b.babyId === selectedBabyId);

  useEffect(() => {
    if (!isFetched) return;
    if (babies.length > 0 && !isValid) {
      setSelectedBabyId(babies[0].babyId);
    } else if (babies.length === 0 && selectedBabyId) {
      setSelectedBabyId(null);
    }
  }, [babies, isFetched, isValid, selectedBabyId, setSelectedBabyId]);

  return {
    babies,
    selectedBabyId: isValid ? selectedBabyId : null,
    isFetched,
    // True only while an actual network fetch is in flight (false when the
    // query is paused offline) — safe to gate UI on without stranding
    // offline users behind a spinner.
    isLoading,
    // The query's status is 'success': the server's list is in. False while
    // loading or paused offline (`babies` is then only the empty default) and
    // after a failed fetch — useEntitlement's `settled` waits for it
    // (docs/v151-spec.md §6.2).
    isSuccess: status === 'success',
  };
}
